import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Autocomplete, Box, Card, Typography, TextField, Button, Paper, Chip, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
    Pagination
} from '@mui/material';
import {
    Refresh as RefreshIcon, Visibility as PreviewIcon,
    PlayArrow as PlayArrowIcon, Undo as UndoIcon, WarningAmber as WarningIcon
} from '@mui/icons-material';
import { API_BASE } from '../config';

interface ReportRow {
    email: string;
    name: string;
    signupDate: string;
    totalInvested: number;
    totalEarned: number;
    remaining: number;
}
interface Summary {
    totalUsers: number;
    totalInvested: number;
    totalEarned: number;
    totalRemaining: number;
    eligibleCount: number;
    eligibleRemainingSum: number;
}
interface PreviewUser {
    email: string; name: string; remaining: number; outstanding?: number; payout: number; priorPaid: number;
    totalInvested: number; totalEarned: number;
}
interface Operation {
    operationId: string; totalCount: number; paidCount: number; rolledBackCount: number;
    totalPaid: number; percentage: number; createdAt: string; status: string; adminEmail?: string;
}

const money = (n: number | string) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RecoveryPayout: React.FC = () => {
    const getToken = () => localStorage.getItem('token');
    const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

    // Filters
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
    const [search, setSearch] = useState('');

    // Report state
    const [rows, setRows] = useState<ReportRow[]>([]);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);

    // Payout state
    const [percentage, setPercentage] = useState('');
    // Accounts the admin wants left out of this payout. Chosen from a search over
    // the accounts that actually fall in the selected date range, so an address
    // that does not exist can never be excluded by accident.
    const [excluded, setExcluded] = useState<string[]>([]);
    const [excludeInput, setExcludeInput] = useState('');
    const [excludeOptions, setExcludeOptions] = useState<{ email: string; name: string }[]>([]);
    const [excludeSearching, setExcludeSearching] = useState(false);
    // Accounts pulled in on top of the date range — for people who signed up
    // earlier but still need paying. Searches every account, not just the range.
    const [included, setIncluded] = useState<string[]>([]);
    const [includeInput, setIncludeInput] = useState('');
    const [includeOptions, setIncludeOptions] = useState<{ email: string; name: string }[]>([]);
    const [includeSearching, setIncludeSearching] = useState(false);
    // Saved recipient lists — pick one to repeat a payout audience without
    // re-entering dates and names every time.
    const [lists, setLists] = useState<{ _id: string; name: string; description?: string; emailCount: number }[]>([]);
    const [listId, setListId] = useState('');
    const [saveName, setSaveName] = useState('');
    const [savingList, setSavingList] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [preview, setPreview] = useState<{ eligibleCount: number; totalPayout: number; percentage: number; users: PreviewUser[] } | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [confirmWord, setConfirmWord] = useState('');
    const [executing, setExecuting] = useState(false);

    // Operations
    const [operations, setOperations] = useState<Operation[]>([]);
    const [rollbackTarget, setRollbackTarget] = useState<string | null>(null);
    const [rollbackLoading, setRollbackLoading] = useState(false);

    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const fetchReport = useCallback(async (pg = 1) => {
        setLoading(true); setError(null);
        try {
            const params = new URLSearchParams({ page: String(pg), limit: '20' });
            if (fromDate) params.append('fromDate', fromDate);
            if (toDate) params.append('toDate', toDate);
            if (search.trim()) params.append('search', search.trim());
            const res = await axios.get(`${API_BASE}/api/recovery/report?${params}`, auth());
            if (res.data.status === 'success') {
                setRows(res.data.rows || []);
                setSummary(res.data.summary || null);
                setPage(res.data.page || 1);
                setTotalPages(res.data.totalPages || 1);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load report');
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, search]);

    const fetchOperations = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE}/api/recovery/operations`, auth());
            if (res.data.status === 'success') setOperations(res.data.operations || []);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { fetchReport(1); fetchOperations(); /* eslint-disable-next-line */ }, []);

    // Live estimate from already-loaded summary
    const pctNum = parseFloat(percentage);
    const excludeEmails = excluded;
    const includeEmails = included;

    const fetchLists = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE}/api/recovery/lists`, auth());
            setLists(res.data?.lists || []);
        } catch { /* the page still works without saved lists */ }
    }, []);
    useEffect(() => { fetchLists(); }, [fetchLists]);

    // Look the typed text up against the accounts in the selected date range.
    // Debounced so a fast typist does not fire a request per keystroke.
    useEffect(() => {
        const q = excludeInput.trim();
        if (q.length < 2) { setExcludeOptions([]); return; }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setExcludeSearching(true);
            try {
                const params = new URLSearchParams({ q });
                if (fromDate) params.append('fromDate', fromDate);
                if (toDate) params.append('toDate', toDate);
                const res = await axios.get(`${API_BASE}/api/recovery/search-users?${params}`, auth());
                if (!cancelled) setExcludeOptions(res.data?.users || []);
            } catch {
                if (!cancelled) setExcludeOptions([]);
            } finally {
                if (!cancelled) setExcludeSearching(false);
            }
        }, 350);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [excludeInput, fromDate, toDate]);

    // Same lookup for the "add extra accounts" picker, but across every account.
    useEffect(() => {
        const q = includeInput.trim();
        if (q.length < 2) { setIncludeOptions([]); return; }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setIncludeSearching(true);
            try {
                const params = new URLSearchParams({ q, ignoreRange: 'true' });
                const res = await axios.get(`${API_BASE}/api/recovery/search-users?${params}`, auth());
                if (!cancelled) setIncludeOptions(res.data?.users || []);
            } catch {
                if (!cancelled) setIncludeOptions([]);
            } finally {
                if (!cancelled) setIncludeSearching(false);
            }
        }, 350);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [includeInput]);
    const liveEstimate = summary && pctNum > 0 && pctNum <= 100
        ? Math.round(summary.eligibleRemainingSum * pctNum) / 100
        : 0;

    const handlePreview = async () => {
        if (!(pctNum > 0 && pctNum <= 100)) { setError('Enter a percentage between 0 and 100'); return; }
        setPreviewLoading(true); setError(null);
        try {
            const res = await axios.post(`${API_BASE}/api/recovery/payout/preview`,
                { fromDate: fromDate || undefined, toDate: toDate || undefined, percentage: pctNum, excludeEmails, includeEmails, listId: listId || undefined }, auth());
            if (res.data.status === 'success') {
                setPreview(res.data);
                setShowPreview(true);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Preview failed');
        } finally {
            setPreviewLoading(false);
        }
    };

    // Saves whoever the current preview would pay, so the same audience can be
    // reused later without rebuilding the filters.
    const handleSaveList = async () => {
        const name = saveName.trim();
        if (!name) { setError('Give the list a name first'); return; }
        if (!preview?.users?.length) { setError('Run a preview first — the list is saved from its result'); return; }
        setSavingList(true); setError(null);
        try {
            const emails = preview.users.map((u: any) => u.email);
            const res = await axios.post(`${API_BASE}/api/recovery/lists`,
                { name, description: `${emails.length} accounts, saved from a ${preview.percentage}% preview`, emails }, auth());
            setSuccess(`Saved "${res.data?.list?.name}" with ${res.data?.list?.emailCount} accounts`);
            setSaveName('');
            fetchLists();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Could not save the list');
        } finally { setSavingList(false); }
    };

    const handleExecute = async () => {
        setExecuting(true); setError(null);
        try {
            const res = await axios.post(`${API_BASE}/api/recovery/payout/execute`,
                { fromDate: fromDate || undefined, toDate: toDate || undefined, percentage: pctNum, excludeEmails, includeEmails, listId: listId || undefined }, auth());
            if (res.data.status === 'success') {
                setSuccess(`Payout complete — ${res.data.paidCount} users credited, total ${money(res.data.totalPaid)}. Operation ${res.data.operationId}`);
                setShowWarning(false); setShowPreview(false); setConfirmWord(''); setPercentage('');
                fetchReport(page); fetchOperations();
                setTimeout(() => setSuccess(null), 9000);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Execute failed');
        } finally {
            setExecuting(false);
        }
    };

    const handleRollback = async () => {
        if (!rollbackTarget) return;
        setRollbackLoading(true); setError(null);
        try {
            const res = await axios.post(`${API_BASE}/api/recovery/payout/rollback`,
                { operationId: rollbackTarget }, auth());
            if (res.data.status === 'success') {
                setSuccess(`Rolled back ${res.data.rolledBackCount} payouts for operation ${rollbackTarget.slice(0, 8)}...`);
                setRollbackTarget(null);
                fetchReport(page); fetchOperations();
                setTimeout(() => setSuccess(null), 7000);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Rollback failed');
        } finally {
            setRollbackLoading(false);
        }
    };

    const SummaryCard: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 150, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h5" fontWeight={700} sx={{ color: color || 'text.primary' }}>{value}</Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Paper>
    );

    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>Recovery Payout</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Capital-recovery report (Invested / Earned / Remaining) with percentage-based wallet payout.
                    </Typography>
                </Box>
                <Button startIcon={<RefreshIcon />} variant="contained" size="small" onClick={() => { fetchReport(1); fetchOperations(); }} disabled={loading}>Refresh</Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            {/* Filter bar */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 3 }, mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Filter Users by Signup Date</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField label="From (signup)" type="date" size="small" value={fromDate} onChange={e => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} helperText="Empty = earliest" sx={{ width: 200 }} />
                    <TextField label="To (signup)" type="date" size="small" value={toDate} onChange={e => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} helperText="Default = today" sx={{ width: 200 }} />
                    <TextField label="Search email" size="small" value={search} onChange={e => setSearch(e.target.value)} sx={{ width: 220 }} />
                    <Button variant="contained" onClick={() => fetchReport(1)} disabled={loading}>Apply / Sync</Button>
                </Box>
            </Card>

            {/* Summary cards */}
            {summary && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    <SummaryCard label="Total Users" value={String(summary.totalUsers)} />
                    <SummaryCard label="Total Invested" value={money(summary.totalInvested)} />
                    <SummaryCard label="Total Earned" value={money(summary.totalEarned)} color="#2e7d32" />
                    <SummaryCard label="Total Remaining" value={money(summary.totalRemaining)} color="#ed6c02" />
                    <SummaryCard label="Eligible (rem>0)" value={String(summary.eligibleCount)} />
                    <SummaryCard label="Eligible Remaining Σ" value={money(summary.eligibleRemainingSum)} color="#ed6c02" />
                </Box>
            )}

            {/* Users table */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 3 }}>
                <TableContainer sx={{ maxHeight: 480 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><b>Email</b></TableCell>
                                <TableCell><b>Name</b></TableCell>
                                <TableCell><b>Signup</b></TableCell>
                                <TableCell align="right"><b>Invested</b></TableCell>
                                <TableCell align="right"><b>Earned</b></TableCell>
                                <TableCell align="right"><b>Remaining</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><Typography color="text.secondary">No users in this range.</Typography></TableCell></TableRow>
                            ) : rows.map(r => (
                                <TableRow key={r.email} hover>
                                    <TableCell><Typography variant="caption">{r.email}</Typography></TableCell>
                                    <TableCell><Typography variant="caption">{r.name}</Typography></TableCell>
                                    <TableCell><Typography variant="caption">{r.signupDate ? new Date(r.signupDate).toLocaleDateString() : '-'}</Typography></TableCell>
                                    <TableCell align="right">{money(r.totalInvested)}</TableCell>
                                    <TableCell align="right" sx={{ color: '#2e7d32' }}>{money(r.totalEarned)}</TableCell>
                                    <TableCell align="right">
                                        <Chip size="small" label={money(r.remaining)}
                                            color={r.remaining > 0 ? 'warning' : 'success'}
                                            variant={r.remaining > 0 ? 'filled' : 'outlined'} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <Pagination count={totalPages} page={page} onChange={(_, p) => fetchReport(p)} size="small" />
                    </Box>
                )}
            </Card>

            {/* Payout panel */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'warning.main', p: { xs: 2, sm: 3 }, mb: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>Percentage Payout</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Credits each eligible user with <strong>(total loss − recovery already paid) × percentage</strong> to their wallet. <strong>Total loss never changes</strong> — it is invested minus earned, and a payout is not earnings. The deduction shows up in <em>Outstanding</em>, so running this again never pays twice on the same shortfall. Rolled-back payouts do not count. Reversible via Rollback.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField label="Percentage (%)" type="number" size="small" value={percentage}
                        onChange={e => setPercentage(e.target.value)} inputProps={{ min: 0, max: 100, step: 0.5 }}
                        placeholder="e.g. 25" sx={{ width: 180 }} />
                    {/* The estimate is worked out from the date-range report above, so it
                        says nothing about a saved list — showing it there would name a
                        total that has nothing to do with who is about to be paid. */}
                    {listId ? (
                        <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2 }}>
                            <Typography variant="body2" fontWeight={700} color="text.secondary">
                                Estimate not shown for a saved list
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Run Preview to see the real total for this list
                            </Typography>
                        </Paper>
                    ) : summary && pctNum > 0 && pctNum <= 100 ? (
                        <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: 'warning.50' }}>
                            <Typography variant="body2" fontWeight={700} color="warning.dark">
                                Estimated total payout: {money(liveEstimate)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {summary.eligibleCount} eligible users · avg {money(summary.eligibleCount ? liveEstimate / summary.eligibleCount : 0)}/user
                            </Typography>
                        </Paper>
                    ) : null}
                    <Button variant="outlined" startIcon={previewLoading ? <CircularProgress size={16} /> : <PreviewIcon />}
                        onClick={handlePreview} disabled={previewLoading || !(pctNum > 0)}>
                        Preview Payout
                    </Button>
                </Box>

                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <TextField
                        select size="small" label="Saved list" sx={{ minWidth: 320 }}
                        value={listId}
                        onChange={e => setListId(e.target.value)}
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        helperText={
                            listId
                                ? 'Using the saved list — the date range above is ignored'
                                : 'Pick a saved list to repeat a previous audience'
                        }
                    >
                        <option value="">— none (use date range) —</option>
                        {lists.map(l => (
                            <option key={l._id} value={l._id}>{l.name} ({l.emailCount})</option>
                        ))}
                    </TextField>

                    <TextField
                        size="small" label="Save current preview as…" sx={{ minWidth: 260 }}
                        value={saveName}
                        onChange={e => setSaveName(e.target.value)}
                        placeholder="e.g. Loss 25%+ Jan–Mar"
                        helperText="Run a preview first, then save who it would pay"
                    />
                    <Button variant="outlined" sx={{ mt: 0.5 }}
                        onClick={handleSaveList}
                        disabled={savingList || !saveName.trim() || !preview?.users?.length}
                        startIcon={savingList ? <CircularProgress size={16} /> : undefined}>
                        Save list
                    </Button>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={includeOptions.map(o => o.email)}
                        value={included}
                        onChange={(_e, val) => setIncluded(val as string[])}
                        inputValue={includeInput}
                        onInputChange={(_e, val) => setIncludeInput(val)}
                        loading={includeSearching}
                        filterOptions={x => x}
                        noOptionsText={
                            includeInput.trim().length < 2
                                ? 'Type at least 2 characters to search'
                                : includeSearching ? 'Searching…' : 'No account with that name or email'
                        }
                        renderOption={(props, option) => {
                            const hit = includeOptions.find(o => o.email === option);
                            return (
                                <li {...props} key={option}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>{option}</Typography>
                                        {hit?.name && (
                                            <Typography variant="caption" color="text.secondary">{hit.name}</Typography>
                                        )}
                                    </Box>
                                </li>
                            );
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip {...getTagProps({ index })} key={option} label={option} size="small" color="success" variant="outlined" />
                            ))
                        }
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Add extra accounts (optional)"
                                placeholder={included.length ? '' : 'Search any account to add…'}
                                helperText={
                                    included.length
                                        ? `${included.length} account(s) added on top of the date range`
                                        : 'Searches every account, not just the date range — use it for people who signed up earlier.'
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {includeSearching ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={excludeOptions.map(o => o.email)}
                        value={excluded}
                        onChange={(_e, val) => setExcluded(val as string[])}
                        inputValue={excludeInput}
                        onInputChange={(_e, val) => setExcludeInput(val)}
                        loading={excludeSearching}
                        // Only accounts returned by the search can be picked, so a
                        // mistyped address cannot silently end up on the list.
                        filterOptions={x => x}
                        noOptionsText={
                            excludeInput.trim().length < 2
                                ? 'Type at least 2 characters to search'
                                : excludeSearching ? 'Searching…' : 'No account matches in this date range'
                        }
                        renderOption={(props, option) => {
                            const hit = excludeOptions.find(o => o.email === option);
                            return (
                                <li {...props} key={option}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>{option}</Typography>
                                        {hit?.name && (
                                            <Typography variant="caption" color="text.secondary">{hit.name}</Typography>
                                        )}
                                    </Box>
                                </li>
                            );
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip {...getTagProps({ index })} key={option} label={option} size="small" color="warning" variant="outlined" />
                            ))
                        }
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Exclude these accounts (optional)"
                                placeholder={excluded.length ? '' : 'Type a name or email to search…'}
                                helperText={
                                    excluded.length
                                        ? `${excluded.length} account(s) will be skipped in preview and payout`
                                        : 'Search and pick the accounts to leave out. Blank = everyone in the date range.'
                                }
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {excludeSearching ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>
            </Card>

            {/* Past operations */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, pb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>Past Operations</Typography>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell><b>Operation</b></TableCell>
                                <TableCell><b>Date</b></TableCell>
                                <TableCell align="center"><b>%</b></TableCell>
                                <TableCell align="center"><b>Paid</b></TableCell>
                                <TableCell align="right"><b>Total</b></TableCell>
                                <TableCell><b>Status</b></TableCell>
                                <TableCell align="right"><b>Action</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {operations.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No operations yet.</Typography></TableCell></TableRow>
                            ) : operations.map(op => (
                                <TableRow key={op.operationId} sx={{ opacity: op.status === 'ROLLED_BACK' ? 0.5 : 1 }}>
                                    <TableCell><Typography variant="caption" fontFamily="monospace">{op.operationId.slice(0, 8)}...</Typography></TableCell>
                                    <TableCell><Typography variant="caption">{new Date(op.createdAt).toLocaleString()}</Typography></TableCell>
                                    <TableCell align="center">{op.percentage}%</TableCell>
                                    <TableCell align="center">{op.paidCount}</TableCell>
                                    <TableCell align="right">{money(op.totalPaid)}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={op.status}
                                            color={op.status === 'PAID' ? 'success' : op.status === 'ROLLED_BACK' ? 'default' : 'warning'}
                                            variant={op.status === 'ROLLED_BACK' ? 'outlined' : 'filled'} />
                                    </TableCell>
                                    <TableCell align="right">
                                        {op.status !== 'ROLLED_BACK' && (
                                            <Button size="small" color="error" variant="outlined" startIcon={<UndoIcon />}
                                                onClick={() => setRollbackTarget(op.operationId)}>Rollback</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* Preview dialog */}
            <Dialog open={showPreview} onClose={() => setShowPreview(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Payout Preview ({preview?.percentage}%)</DialogTitle>
                <DialogContent>
                    {preview && (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                <SummaryCard label="Eligible Users" value={String(preview.eligibleCount)} />
                                <SummaryCard label="Total Payout" value={money(preview.totalPayout)} color="#ed6c02" />
                            </Box>
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320, borderRadius: 2 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><b>Email</b></TableCell>
                                            <TableCell align="right"><b>Total Loss</b></TableCell>
                                            <TableCell align="right"><b>Prior Paid</b></TableCell>
                                            <TableCell align="right"><b>Outstanding</b></TableCell>
                                            <TableCell align="right"><b>Payout</b></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {preview.users.slice(0, 200).map(u => (
                                            <TableRow key={u.email}>
                                                <TableCell><Typography variant="caption">{u.email}</Typography></TableCell>
                                                <TableCell align="right">{money(u.remaining)}</TableCell>
                                                <TableCell align="right">
                                                    {u.priorPaid > 0
                                                        ? <Chip size="small" color="warning" variant="outlined" label={money(u.priorPaid)} />
                                                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                                                </TableCell>
                                                {/* Total loss never moves — it is what the account actually lost.
                                                    What the percentage is applied to is this: the loss minus any
                                                    recovery already handed back. */}
                                                <TableCell align="right">{money(u.outstanding ?? u.remaining)}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>{money(u.payout)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {preview.users.length > 200 && (
                                <Typography variant="caption" color="text.secondary">Showing first 200 of {preview.users.length}.</Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowPreview(false)}>Close</Button>
                    <Button color="warning" variant="contained" startIcon={<PlayArrowIcon />}
                        onClick={() => setShowWarning(true)}
                        disabled={!preview || preview.eligibleCount === 0}>
                        Execute Payout
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Warning / confirm dialog */}
            <Dialog open={showWarning} onClose={() => !executing && setShowWarning(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="error" /> Confirm Production Payout
                </DialogTitle>
                <DialogContent>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={700}>
                            This credits REAL money to {preview?.eligibleCount} user wallets, totalling {money(preview?.totalPayout || 0)} at {preview?.percentage}%.
                        </Typography>
                    </Alert>
                    <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
                        <li>A full wallet backup is saved automatically before any write.</li>
                        <li>The operation is logged and can be <strong>rolled back</strong> any time.</li>
                        <li>Only users with <strong>remaining &gt; 0</strong> are paid.</li>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>Type <strong>PAYOUT</strong> to confirm:</Typography>
                    <TextField fullWidth size="small" value={confirmWord} onChange={e => setConfirmWord(e.target.value)} placeholder="PAYOUT" autoFocus />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setShowWarning(false); setConfirmWord(''); }} disabled={executing}>Cancel</Button>
                    <Button color="error" variant="contained"
                        startIcon={executing ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                        disabled={confirmWord !== 'PAYOUT' || executing}
                        onClick={handleExecute}>
                        Execute Payout
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rollback confirm */}
            <Dialog open={!!rollbackTarget} onClose={() => !rollbackLoading && setRollbackTarget(null)}>
                <DialogTitle sx={{ fontWeight: 700 }}>Rollback Operation</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This will debit the exact paid amounts back from each user's wallet and mark the operation reversed.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">{rollbackTarget}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRollbackTarget(null)} disabled={rollbackLoading}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleRollback} disabled={rollbackLoading}
                        startIcon={rollbackLoading ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}>
                        Confirm Rollback
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RecoveryPayout;
